<template>
  <div class="resource-zone">
    <div class="zone-header">
      <h3 class="zone-title">{{ $t('overview.idcResourceZone') }}</h3>
      <div class="zone-controls">
        <el-checkbox
          v-model="onlyUnexpired"
          @change="getIdcStatsData"
          :true-label="'yes'"
          :false-label="'no'"
        >{{ $t('overview.onlyUnexpired') }}</el-checkbox>
      </div>
    </div>

    <div class="location-tabs">
      <div
        v-for="tab in regionsList"
        :key="tab.id"
        class="tab"
        :class="{ active: activeTabId === tab.idc }"
        @click="handleClickTab(tab.idc)"
      >
        {{ tab.datacenterNameShow }}
        <span class="sub-text" :class="{ active: activeTabId === tab.idc }">{{ tab.regionShow }}</span>
      </div>
    </div>

    <div class="resource-grid" v-if="resourceData.length">
      <div class="resource-card" v-for="(card, index) in resourceData" :key="index" v-loading="loading">
        <div class="card-title">{{ card.title }}</div>
        <div v-for="(row, rowIndex) in card.rows" :key="rowIndex">
          <div class="card-row" @click="jumpTo(row.routePath)">
            <span>{{ row.label }}</span>
            <span>
              <b :class="{ 'highlight': row.highlight }" class="value">{{ row.value }}</b>
              {{ row.unit }}
              <span v-if="row.arrow">↑</span>
            </span>
          </div>
          <!-- 如果需要每行都有分割线（除了最后一行），可以使用下面的代码 -->
           <div class="divider" v-if="!row.isTotal && rowIndex < card.rows.length - 1 && card.showRowDivider"></div>
        </div>
      </div>
    </div>
    <el-empty v-else :description="$t('text.noSearch')"></el-empty>
  </div>
</template>

<script>
export default {
  name: 'ResourceZone',
  data() {
    return {
      onlyUnexpired: 'yes',
      activeTabId: '',
      loading: false,
      regionsList: [],
      resourceData: []
    }
  },
  mounted() {
    this.getRegions();
  },

  methods: {
    // 请求tab切换的机房信息
    getRegions() {
      this.$api.getRegions({ all: 1, idcLine: this.GLOBAL.IDCLINE }).then((res) => {
        if (res.errno === 10000 && res.data.idcs.length) {
          this.regionsList = res.data.idcs;
          this.activeTabId = this.regionsList[0].idc;
          this.getIdcStatsData();
        }
      })
    },
    handleClickTab(idc) {
      this.activeTabId = idc;
      this.getIdcStatsData();
    },
    jumpTo(routePath) {
      if (!routePath) return
      this.$router.push({
        path: routePath,
        query: {
          selectRoom: this.activeTabId
        }
      })
    },
    // 请求机房资源统计数据
    getIdcStatsData() {
      // 1. 准备请求参数
      const params = {
        onlyUnexpired: this.onlyUnexpired,
        idc: this.activeTabId
      };

      // 开启加载状态
      this.loading = true;

      // 调用 API 获取数据
      this.$api.getIdcStatsData(params)
        .then(res => {
          // 2. 校验响应状态：确保 errno 为 10000 且 data 存在
          if (res.errno === 10000 && res.data) {

            // --- 数据解构 (Data Destructuring) ---
            // 将深层对象提前解构，简化后续代码中的引用路径
            const {
              cabinet,
              device,
              ip,
              bandwidth,
              directConnect,
              otherResource
            } = res.data;

            // --- 上下文与常量定义 (Context & Constants) ---

            // 简化翻译函数调用，绑定当前上下文
            const t = (key) => this.$t(key);

            // 预定义常用单位，避免在构建行数据时重复调用翻译函数
            const Unit = {
              UNIT: t('rowIdcs.unitText.unit'),   // 台/个
              MBYTE: t('rowIdcs.unitText.mbyte'), // MB
              STRIP: t('rowIdcs.unitText.strip'), // 条
              KM: t('rowIdcs.unitText.km'),       // km
              PARA: t('rowIdcs.unitText.paragraph'), // 段
              AMP: t('rowIdcs.unitText.ampere'),  // A
              CHIP: t('rowIdcs.unitText.chip'),   // 个 (芯片/端口)
              ITEM: t('rowIdcs.unitText.item'),   // 项
              BATCH: t('rowIdcs.unitText.batch')  // 批
            };

            // --- 核心辅助函数 (Helper Functions) ---

            /**
             * 构建单行数据对象
             * @param {String} labelKey - 标签翻译Key
             * @param {Number|String} value - 显示数值
             * @param {String} unitText - 单位文本
             * @param {String} routePath - 路由跳转地址
             */
            const createRow = (labelKey, value, unitText, routePath) => ({
              label: t(labelKey),
              value: value, // 增加空值保护，默认为 0
              unit: unitText,
              routePath
            });

            /**
             * 构建板块对象 (Section)
             * @param {String} titleKey - 标题翻译Key
             * @param {Array} rows - 行数据集合
             * @param {Boolean} showDivider - 是否显示底部分割线 (默认显示)
             */
            const createSection = (titleKey, rows, showDivider = true) => ({
              title: t(titleKey),
              showRowDivider: showDivider,
              rows: rows
            });

            /**
             * 专用构建器：设备统计板块 (包含 租用/自建/小计 逻辑)
             * 复用于服务器、网络设备、存储设备等
             */
            const createDeviceSection = (titleKey, deviceData, routePath) => {
              return createSection(titleKey, [
                createRow('rowIdcs.lease', deviceData.lease, Unit.UNIT, `${routePath}&assetBelong=lease`),
                createRow('rowIdcs.own', deviceData.own, Unit.UNIT, `${routePath}&assetBelong=own`),
                createRow('rowIdcs.sum', deviceData.sum, Unit.UNIT, routePath)
              ]);
            };

            // --- 业务模块数据组装 (Module Assembly) ---
            // 将不同业务模块分开定义，最后合并，提高代码可读性

            // 1. 机柜资源 (Cabinet)
            const cabinetSection = createSection('text.cabinet', [
              createRow('rowIdcs.formal', cabinet.formal, Unit.UNIT, '/cabinet/list?cabinetType=formal'),
              createRow('rowIdcs.reserved', cabinet.reserved, Unit.UNIT, '/cabinet/list?cabinetType=reserved'),
              createRow('rowIdcs.sum', cabinet.sum, Unit.UNIT, '/cabinet/list')
            ]);

            // 2. 硬件设备 (Devices)
            const deviceSections = [
              createDeviceSection('devicetypes.type1', device.server, '/equipment/list?deviceType=server'),
              createDeviceSection('devicetypes.type2', device.network, '/equipment/list?deviceType=network'),
              createDeviceSection('devicetypes.type3', device.storage, '/equipment/list?deviceType=storage'),
              createDeviceSection('devicetypes.type4', device.other, '/equipment/list?deviceType=other')
            ];

            // 3. IP 资源 (IP Address)
            const ipSection = createSection('overview.ipTotal', [
              createRow('rowIdcs.ipv4', ip.ipv4, Unit.UNIT, '/ipAddress/list?ipType=IPV4'),
              createRow('rowIdcs.ipv6', ip.ipv6, Unit.PARA, '/ipAddress/list?ipType=IPV6')
            ]);

            // 4. 带宽资源 (Bandwidth)
            const bandwidthSections = [
              // BGP 带宽
              createSection('bandWidth.bd', [
                createRow('rowIdcs.dynamicBGP', bandwidth.dynamicBGP, Unit.MBYTE, '/bandWidth/list?lineTypeConsole=dynamicBGP'),
                createRow('rowIdcs.staticBGP', bandwidth.staticBGP, Unit.MBYTE, '/bandWidth/list?lineTypeConsole=staticBGP'),
                createRow('rowIdcs.announcedBGP', bandwidth.announcedBGP, Unit.MBYTE, '/bandWidth/list?lineTypeConsole=announcedBGP')
              ]),
              // 运营商带宽
              createSection('bandWidth.bd', [
                createRow('rowIdcs.mobile', bandwidth.mobile, Unit.MBYTE, '/bandWidth/list?lineTypeConsole=mobile'),
                createRow('rowIdcs.unicom', bandwidth.unicom, Unit.MBYTE, '/bandWidth/list?lineTypeConsole=unicom'),
                createRow('rowIdcs.telecom', bandwidth.telecom, Unit.MBYTE, '/bandWidth/list?lineTypeConsole=telecom')
              ])
            ];

            // 5. 专线连接 (Direct Connect)
            const directConnectSection = createSection('rowIdcs.directConnect', [
              createRow('rowIdcs.directConnect', directConnect.directConnect, Unit.MBYTE, '/directConnects/list?resourceType=directConnect'),
              createRow('rowIdcs.bareFibre', directConnect.bareFibre, Unit.KM, '/directConnects/list?resourceType=bareFibre'),
              createRow('rowIdcs.transmissionLink', directConnect.transmissionLink, Unit.STRIP, '/directConnects/list?resourceType=transmissionLink')
            ]);

            // 6. 其他杂项资源 (Other Resources)
            // 注意：此类资源在UI设计上通常不显示分割线 (showDivider = false)
            const otherResourceSections = [
              // 电力
              createSection('rowIdcs.power', [
                createRow('rowIdcs.electricity', otherResource.power, Unit.AMP, '/otherResource/list?productType=6')
              ], false),
              // IDC端口
              createSection('rowIdcs.idcPort', [
                createRow('rowIdcs.idcPort', otherResource.idcPort, Unit.CHIP, '/otherResource/list?productType=7')
              ], false),
              // 设备端口
              createSection('rowIdcs.devicePort', [
                createRow('rowIdcs.devicePort', otherResource.devicePort, Unit.UNIT, '/otherResource/list?productType=8')
              ], false),
              // 配件
              createSection('rowIdcs.accessory', [
                createRow('rowIdcs.accessory', otherResource.accessory, Unit.UNIT, '/otherResource/list?productType=12')
              ], false),
              // 线缆
              createSection('rowIdcs.cable', [
                createRow('rowIdcs.cable', otherResource.cable, Unit.STRIP, '/otherResource/list?productType=10')
              ], false),
              // 增值服务
              createSection('rowIdcs.vas', [
                createRow('rowIdcs.vas', otherResource.vas, Unit.ITEM, '/otherResource/list?productType=9')
              ], false),
              // 预留费
              createSection('rowIdcs.reservationFee', [
                createRow('rowIdcs.reservationFee', otherResource.reservationFee, Unit.UNIT, '/otherResource/list?productType=16')
              ], false),
              // 一次性服务
              createSection('rowIdcs.oneTimeService', [
                createRow('rowIdcs.oneTimeService', otherResource.oneTimeService, Unit.BATCH, '/otherResource/list?productType=13')
              ], false)
            ];

            // --- 最终合并 (Final Merge) ---

            // 辅助函数：判断板块是否应该展示
            const shouldShowSection = (section) => {
              if (!section) return false;

              // 1. 优先检查是否有"总计"行 (针对机柜、设备)
              const sumRow = section.rows.find(row => row.label === t('rowIdcs.sum'));
              if (sumRow) {
                // 如果有总计行，且值为0，整个板块不展示
                return Number(sumRow.value) > 0;
              }

              // 2. 没有"总计"行，检查所有行数值之和 (针对IP、带宽、专线、其他资源)
              // 只要有任意一行数值大于0，就展示
              const total = section.rows.reduce((acc, row) => acc + Number(row.value || 0), 0);
              return total > 0;
            };

            const allItems = [
              cabinetSection,
              ...deviceSections,
              ipSection,
              ...bandwidthSections,
              directConnectSection,
              ...otherResourceSections
            ];

            // 统一过滤逻辑：总计为0或所有行均为0时不展示
            // this.resourceData = allItems;
            this.resourceData = allItems.filter(shouldShowSection);
          }
        })
        .finally(() => {
          // 无论成功或失败，关闭加载状态
          this.loading = false;
        });
    }
  }
}
</script>

<style lang="scss" scoped>
.resource-zone {
  background: #fff;
  padding: 24px;
  border-radius: 8px;

  .zone-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .zone-title {
      font-size: 14px;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.85);
      margin: 0;
    }

    .zone-controls {
      font-size: 14px;
      color: rgba(0, 0, 0, 0.65);

      label {
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 4px;
      }
      /deep/ .el-checkbox__inner {
        background-color: #fff !important;
        &::after {
          border: 1px solid #2C68FF;
          border-left: 0;
          border-top: 0;
          height: 7px;
          left: 4px;
          position: absolute;
          top: 1px;
        }
      }
    }
  }

  .location-tabs {
    display: flex;
    gap: 30px;
    border-bottom: 1px solid #ebeef5;
    margin-bottom: 20px;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;
    // scrollbar-width: thin;

    &::-webkit-scrollbar {
      height: 2px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.15);
      border-radius: 3px;
    }

    .tab {
      flex: 0 0 auto;
      padding-bottom: 12px;
      cursor: pointer;
      font-size: 14px;
      color: rgba(0, 0, 0, 0.85);
      position: relative;
      &:hover {
        .sub-text {
          color: #1D41FF;
        }
        color: #1D41FF;
      }

      &.active {
        color: #1D41FF;
        font-weight: 500;

        &::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 0;
          width: 100%;
          height: 2px;
          background-color: #1D41FF;
        }
      }

      .sub-text {
        font-size: 12px;
        color: #909399;
        font-weight: normal;
        margin-top: 4px;
        display: block;
        text-align: center;
        &.active, &:hover {
          color: #1D41FF;
        }
      }
    }
  }

  .resource-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;

    .resource-card {
      // min-height: 144px;
      // border: 1px solid rgba(0, 0, 0, 0.06);
      border-radius: 8px;
      padding: 16px;
      // background: #fff;
      background-color: rgba(0, 0, 0, 0.02);
      // box-shadow: 0 1px 4px 1px rgba(0, 0, 0, 0.04), 0 3px 6px 0 rgba(0, 0, 0, 0.03), 0 1px 4px -3px rgba(0, 0, 0, 0.02);
      &:hover {
        border-color: #1D41FF;
        // box-shadow: 0 3px 8px 3px rgba(0, 0, 0, 0.04), 0 2px 6px 0 rgba(0, 0, 0, 0.03), 0 1px 4px -3px rgba(0, 0, 0, 0.02);
      }

      .card-title {
        font-size: 14px;
        font-weight: 600;
        color: rgba(0, 0, 0, 0.85);
        margin-bottom: 16px;
        text-align: center;
      }

      .card-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
        font-size: 13px;
        color: rgba(0, 0, 0, 0.65);
        cursor: pointer;
        &:last-child {
             margin-bottom: 0;
        }

        b {
          color: rgba(0, 0, 0, 0.85);
          font-weight: 600;
          margin-right: 2px;

          &.highlight {
            color: #1D41FF;
          }
        }

        &:hover {
            b {
                color: #1D41FF;
            }
        }
      }

      .divider {
        height: 1px;
        background-color: rgba(0, 0, 0, 0.06); // 修改为用户指定的颜色，或者保持一致
        margin: 8px 0;
        // border-style: dashed; // 用户可能希望实线分割，既然是“加分割线”且无特殊说明，通常是实线，或者保持原有的虚线但颜色变浅。用户说“色值rgba(0, 0, 0, 0.06);”，这应该也适用于分割线？
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/resourceOverview/components/ResourceZone.vue